/**
 * Compresor de imagenes en el navegador (client-side).
 * =====================================================
 * Redimensiona a un maximo de 800x800 (solo reduce, nunca agranda),
 * convierte a WebP (con fallback a JPEG si el navegador no lo soporta)
 * y baja la calidad en cascada hasta quedar por debajo de `maxBytes`.
 *
 * El objetivo es que CUALQUIER imagen subida por el usuario llegue ya
 * optimizada y pequena al backend, sin bloquear por tamano. Respeta la
 * orientacion EXIF cuando el navegador lo permite.
 *
 * Uso:
 *   const compressed = await compressImage(file);          // File WebP
 *   const compressed = await compressImage(file, { maxBytes: 200*1024 });
 */

const DEFAULT_OPTIONS = {
  maxSize: 800,        // lado maximo en pixeles
  maxBytes: 300 * 1024, // peso objetivo (300 KB)
  quality: 0.85,       // calidad inicial
  minQuality: 0.4,     // piso de calidad en la busqueda
};

// Pasos de calidad para el bucle de compresion progresiva.
const QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55, 0.45];

function supportsWebP() {
  try {
    const canvas = document.createElement('canvas');
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch {
    return false;
  }
}

// Carga la imagen como un ImageBitmap (aplica orientacion EXIF) o HTMLImageElement.
async function loadImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // continue con el fallback
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen'));
    };
    img.src = url;
  });
}

function toBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob fallo'))),
      mime,
      quality
    );
  });
}

/**
 * Comprime una imagen y devuelve un File listo para subir.
 * @param {File|Blob} file imagen original
 * @param {object} overrides opciones (maxSize, maxBytes, quality...)
 * @returns {Promise<File>} File optimizado (WebP o JPEG)
 */
export async function compressImage(file, overrides = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...overrides };

  // Nada que comprimir (o no es imagen): se devuelve tal cual.
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return file;
  }

  let bitmap;
  try {
    bitmap = await loadImage(file);
  } catch {
    // Si no se puede decodificar, no bloqueamos: se envia el original.
    return file;
  }

  const natW = bitmap.width || bitmap.naturalWidth;
  const natH = bitmap.height || bitmap.naturalHeight;

  // Redimension conservando la proporcion (solo reduce).
  let width = natW;
  let height = natH;
  if (width > opts.maxSize || height > opts.maxSize) {
    if (width >= height) {
      height = Math.round((height * opts.maxSize) / width);
      width = opts.maxSize;
    } else {
      width = Math.round((width * opts.maxSize) / height);
      height = opts.maxSize;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, width, height);
  if (bitmap.close) {
    bitmap.close();
  }

  const mime = supportsWebP() ? 'image/webp' : 'image/jpeg';

  // Compresion progresiva: baja la calidad hasta quedar bajo maxBytes.
  let blob = await toBlob(canvas, mime, opts.quality);
  for (const q of QUALITY_STEPS) {
    if (blob.size <= opts.maxBytes) break;
    blob = await toBlob(canvas, mime, q);
  }

  // Si por algun motivo la compresion salio mas pesada que el original,
  // se conserva el original para no empeorar nada.
  if (blob.size >= file.size) {
    return file;
  }

  const ext = mime === 'image/webp' ? 'webp' : 'jpg';
  const baseName = (file.name || 'imagen').replace(/\.[^.]+$/, '');
  const newName = `${baseName}.${ext}`;

  return new File([blob], newName, { type: mime, lastModified: Date.now() });
}

export default compressImage;
